import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Floating } from "@/components/ui/floating";

export function Popover({
  trigger,
  triggerLabel,
  triggerClassName = "",
  align = "start",
  role = "dialog",
  label,
  className = "",
  children,
}: {
  trigger: ReactNode;
  triggerLabel?: string;
  triggerClassName?: string;
  align?: "start" | "end";
  role?: "dialog" | "menu";
  /** The panel's accessible name. */
  label: string;
  className?: string;
  children: ReactNode | ((close: () => void) => ReactNode);
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => setOpen(false);
  return (
    <span ref={root} className="relative inline-flex">
      <button
        type="button"
        className={triggerClassName}
        aria-label={triggerLabel}
        aria-haspopup={role}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={(e) => (e.stopPropagation(), setOpen((o) => !o))}
      >
        {trigger}
      </button>
      {open && (
        <Floating
          anchor={root}
          onDismiss={close}
          align={align}
          id={id}
          role={role}
          aria-label={label}
          className={`text-left ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {typeof children === "function" ? children(close) : children}
        </Floating>
      )}
    </span>
  );
}
